import { useState, useEffect } from "react";
import { apiClient, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line
} from "recharts";
import { 
  FileText,
  Download,
  RefreshCw,
  Brain,
  Truck,
  Users,
  Wrench,
  Fuel,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  BarChart3,
  PieChart as PieChartIcon,
  FileSpreadsheet,
  Sparkles,
  Calendar,
  Loader2
} from "lucide-react";
import { toast } from "sonner";

const formatCurrency = (value) => {
  if (!value && value !== 0) return "TZS 0";
  return new Intl.NumberFormat("en-TZ", {
    style: "currency",
    currency: "TZS",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatShortCurrency = (value) => {
  if (!value && value !== 0) return "0";
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
  return value.toFixed(0);
};

const COLORS = ['hsl(var(--chart-1))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))', 'hsl(var(--chart-4))', 'hsl(var(--chart-5))'];

export default function ReportsPage() {
  const { user } = useAuth();
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [fleetSummary, setFleetSummary] = useState(null);
  const [costAnalysis, setCostAnalysis] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("summary");

  useEffect(() => {
    fetchReports();
  }, [selectedYear]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const [summaryRes, costRes] = await Promise.all([
        apiClient.get("/reports/fleet-summary"),
        apiClient.get(`/reports/cost-analysis?year=${selectedYear}`)
      ]);
      setFleetSummary(summaryRes.data);
      setCostAnalysis(costRes.data);
    } catch (error) {
      toast.error("Failed to fetch reports");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const generateAiAnalysis = async () => {
    setAiLoading(true);
    try {
      const res = await apiClient.post("/reports/ai-analysis");
      setAiAnalysis(res.data);
      toast.success("AI analysis generated successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate AI analysis");
    } finally {
      setAiLoading(false);
    }
  };

  const handleExport = async (reportType) => {
    try {
      const response = await apiClient.get(`/reports/export/${reportType}?year=${selectedYear}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType}_report_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${reportType} report exported successfully`);
    } catch (error) {
      toast.error("Failed to export report");
    }
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color = "text-primary" }) => (
    <Card className="bg-card border-border rounded-sm">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-mono text-muted-foreground uppercase">{title}</p>
            <p className={`text-2xl font-heading font-bold ${color} mt-1`}>{value}</p>
            {subtitle && <p className="text-xs font-mono text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <div className={`w-10 h-10 rounded-sm bg-primary/10 border border-primary/30 flex items-center justify-center`}>
            <Icon className={`w-5 h-5 ${color}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="space-y-6" data-testid="reports-loading">
        <div className="skeleton-tactical h-8 w-48 rounded" />
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <div key={i} className="skeleton-tactical h-24 rounded" />)}
        </div>
        <div className="skeleton-tactical h-96 w-full rounded" />
      </div>
    );
  }

  // Prepare chart data
  const vehicleTypeData = fleetSummary?.vehicle_statistics?.by_type 
    ? Object.entries(fleetSummary.vehicle_statistics.by_type).map(([name, value]) => ({ name, value }))
    : [];

  const vehicleMakeData = fleetSummary?.vehicle_statistics?.by_make
    ? Object.entries(fleetSummary.vehicle_statistics.by_make).map(([name, value]) => ({ name, value }))
    : [];

  const monthlyData = costAnalysis?.monthly_breakdown?.map(m => ({
    name: m.month.split('-')[1],
    Maintenance: m.maintenance,
    Fuel: m.fuel,
    Repairs: m.repairs
  })) || [];

  const maintenanceTypeData = costAnalysis?.maintenance_by_type?.slice(0, 6) || [];
  const repairTypeData = costAnalysis?.repairs_by_type?.slice(0, 6) || [];

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight text-foreground">
            REPORTS & ANALYSIS
          </h1>
          <p className="text-sm font-mono text-muted-foreground mt-1">
            Comprehensive fleet reports with AI-powered insights
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-32 bg-input border-border rounded-sm font-mono" data-testid="year-selector">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card border-border rounded-sm">
              {[currentYear - 1, currentYear, currentYear + 1].map(year => (
                <SelectItem key={year} value={year.toString()} className="font-mono">{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={fetchReports}
            variant="outline"
            className="border-border rounded-sm"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4 bg-background border border-border rounded-sm">
          <TabsTrigger value="summary" className="font-heading text-xs data-[state=active]:bg-primary/10">
            <BarChart3 className="w-4 h-4 mr-2" />
            SUMMARY
          </TabsTrigger>
          <TabsTrigger value="costs" className="font-heading text-xs data-[state=active]:bg-primary/10">
            <DollarSign className="w-4 h-4 mr-2" />
            COST ANALYSIS
          </TabsTrigger>
          <TabsTrigger value="ai" className="font-heading text-xs data-[state=active]:bg-primary/10">
            <Brain className="w-4 h-4 mr-2" />
            AI INSIGHTS
          </TabsTrigger>
          <TabsTrigger value="export" className="font-heading text-xs data-[state=active]:bg-primary/10">
            <Download className="w-4 h-4 mr-2" />
            EXPORT
          </TabsTrigger>
        </TabsList>

        {/* SUMMARY TAB */}
        <TabsContent value="summary" className="space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard 
              title="Total Vehicles" 
              value={fleetSummary?.vehicle_statistics?.total || 0}
              subtitle={`${fleetSummary?.vehicle_statistics?.by_status?.active || 0} active`}
              icon={Truck}
            />
            <StatCard 
              title="Total Drivers" 
              value={fleetSummary?.driver_statistics?.total || 0}
              subtitle={`${fleetSummary?.driver_statistics?.license_expired || 0} licenses expired`}
              icon={Users}
              color={fleetSummary?.driver_statistics?.license_expired > 0 ? "text-red-400" : "text-primary"}
            />
            <StatCard 
              title="Total Fleet Mileage" 
              value={`${(fleetSummary?.vehicle_statistics?.total_mileage / 1000).toFixed(0)}K km`}
              subtitle={`Avg: ${(fleetSummary?.vehicle_statistics?.average_mileage / 1000).toFixed(0)}K per vehicle`}
              icon={TrendingUp}
            />
            <StatCard 
              title="Total Costs" 
              value={formatShortCurrency(fleetSummary?.cost_statistics?.grand_total || 0)}
              subtitle="All categories"
              icon={DollarSign}
            />
          </div>

          {/* Charts Row 1 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Vehicle Types Pie Chart */}
            <Card className="bg-card border-border rounded-sm">
              <CardHeader className="border-b border-border pb-3">
                <CardTitle className="font-heading text-sm tracking-wide flex items-center gap-2">
                  <PieChartIcon className="w-4 h-4 text-primary" />
                  FLEET BY VEHICLE TYPE
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={vehicleTypeData}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {vehicleTypeData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Vehicle Makes Bar Chart */}
            <Card className="bg-card border-border rounded-sm">
              <CardHeader className="border-b border-border pb-3">
                <CardTitle className="font-heading text-sm tracking-wide flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-primary" />
                  FLEET BY MANUFACTURER
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={vehicleMakeData} layout="vertical" margin={{ left: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis type="number" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} />
                      <YAxis type="category" dataKey="name" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} />
                      <Tooltip />
                      <Bar dataKey="value" fill="hsl(var(--primary))" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Status Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Warranty Status */}
            <Card className="bg-card border-border rounded-sm">
              <CardHeader className="border-b border-border pb-3">
                <CardTitle className="font-heading text-sm tracking-wide">WARRANTY STATUS</CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-mono text-muted-foreground">Under Warranty</span>
                  <Badge className="bg-green-500/20 text-green-400 font-mono">
                    {fleetSummary?.vehicle_statistics?.warranty_status?.under_warranty || 0}
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-mono text-muted-foreground">Warranty Expired</span>
                  <Badge className="bg-red-500/20 text-red-400 font-mono">
                    {fleetSummary?.vehicle_statistics?.warranty_status?.expired || 0}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Repair Status */}
            <Card className="bg-card border-border rounded-sm">
              <CardHeader className="border-b border-border pb-3">
                <CardTitle className="font-heading text-sm tracking-wide">REPAIR ORDERS</CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-3">
                {Object.entries(fleetSummary?.repair_statistics?.by_status || {}).map(([status, count]) => (
                  <div key={status} className="flex justify-between items-center">
                    <span className="text-sm font-mono text-muted-foreground capitalize">{status.replace('_', ' ')}</span>
                    <Badge className={`font-mono ${status === 'completed' ? 'bg-green-500/20 text-green-400' : status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-blue-500/20 text-blue-400'}`}>
                      {count}
                    </Badge>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Mission Status */}
            <Card className="bg-card border-border rounded-sm">
              <CardHeader className="border-b border-border pb-3">
                <CardTitle className="font-heading text-sm tracking-wide">MISSIONS</CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-mono text-muted-foreground">Total Missions</span>
                  <Badge className="bg-primary/20 text-primary font-mono">
                    {fleetSummary?.mission_statistics?.total || 0}
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-mono text-muted-foreground">Total Distance</span>
                  <span className="text-sm font-mono text-foreground">
                    {(fleetSummary?.mission_statistics?.total_distance_km || 0).toLocaleString()} km
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* COST ANALYSIS TAB */}
        <TabsContent value="costs" className="space-y-6">
          {/* Cost Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard 
              title="Maintenance" 
              value={formatShortCurrency(costAnalysis?.summary?.total_maintenance || 0)}
              icon={Wrench}
              color="text-blue-400"
            />
            <StatCard 
              title="Fuel" 
              value={formatShortCurrency(costAnalysis?.summary?.total_fuel || 0)}
              subtitle={`${(costAnalysis?.fuel_statistics?.total_liters || 0).toLocaleString()} liters`}
              icon={Fuel}
              color="text-green-400"
            />
            <StatCard 
              title="Repairs" 
              value={formatShortCurrency(costAnalysis?.summary?.total_repairs || 0)}
              icon={AlertTriangle}
              color="text-yellow-400"
            />
            <StatCard 
              title="Grand Total" 
              value={formatShortCurrency(costAnalysis?.summary?.grand_total || 0)}
              icon={DollarSign}
            />
          </div>

          {/* Monthly Trend Chart */}
          <Card className="bg-card border-border rounded-sm">
            <CardHeader className="border-b border-border pb-3">
              <CardTitle className="font-heading text-sm tracking-wide flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-primary" />
                MONTHLY COST BREAKDOWN - {selectedYear}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="name" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} />
                    <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} tickFormatter={(v) => formatShortCurrency(v)} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Bar dataKey="Maintenance" fill="hsl(var(--chart-1))" stackId="a" />
                    <Bar dataKey="Fuel" fill="hsl(var(--chart-2))" stackId="a" />
                    <Bar dataKey="Repairs" fill="hsl(var(--chart-3))" stackId="a" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Cost Breakdown Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Maintenance by Type */}
            <Card className="bg-card border-border rounded-sm">
              <CardHeader className="border-b border-border pb-3">
                <CardTitle className="font-heading text-sm tracking-wide">MAINTENANCE BY TYPE</CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="space-y-3">
                  {maintenanceTypeData.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <span className="text-sm font-mono text-muted-foreground">{item.type}</span>
                      <span className="text-sm font-mono text-foreground">{formatCurrency(item.cost)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Top Spending Vehicles */}
            <Card className="bg-card border-border rounded-sm">
              <CardHeader className="border-b border-border pb-3">
                <CardTitle className="font-heading text-sm tracking-wide">TOP SPENDING VEHICLES</CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="space-y-3">
                  {costAnalysis?.top_spending_vehicles?.slice(0, 5).map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <span className="text-xs font-mono text-muted-foreground truncate max-w-[200px]">{item.name}</span>
                      <span className="text-sm font-mono text-foreground">{formatCurrency(item.total)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* AI INSIGHTS TAB */}
        <TabsContent value="ai" className="space-y-6">
          {/* Generate AI Analysis Button */}
          <Card className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 border-purple-500/30 rounded-sm">
            <CardContent className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-sm bg-purple-500/20 flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h3 className="font-heading text-lg text-foreground">AI-Powered Fleet Analysis</h3>
                  <p className="text-sm font-mono text-muted-foreground">
                    Generate comprehensive insights and recommendations using GPT-4
                  </p>
                </div>
              </div>
              <Button
                onClick={generateAiAnalysis}
                disabled={aiLoading}
                className="bg-purple-600 hover:bg-purple-700 text-white rounded-sm font-heading tracking-wider"
                data-testid="generate-ai-btn"
              >
                {aiLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ANALYZING...
                  </>
                ) : (
                  <>
                    <Brain className="w-4 h-4 mr-2" />
                    GENERATE ANALYSIS
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* AI Analysis Results */}
          {aiAnalysis ? (
            <Card className="bg-card border-border rounded-sm">
              <CardHeader className="border-b border-border pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-sm tracking-wide flex items-center gap-2">
                    <Brain className="w-4 h-4 text-purple-400" />
                    AI FLEET ANALYSIS
                  </CardTitle>
                  <Badge className="bg-purple-500/20 text-purple-400 font-mono text-xs">
                    Generated: {new Date(aiAnalysis.generated_at).toLocaleString()}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <ScrollArea className="h-[600px] pr-4">
                  <div className="prose prose-invert prose-sm max-w-none">
                    <div className="whitespace-pre-wrap font-mono text-sm text-foreground leading-relaxed">
                      {aiAnalysis.analysis}
                    </div>
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-card border-border rounded-sm">
              <CardContent className="p-12 text-center">
                <Brain className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-heading text-lg text-foreground mb-2">No Analysis Generated Yet</h3>
                <p className="text-sm font-mono text-muted-foreground">
                  Click the "Generate Analysis" button above to get AI-powered insights
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* EXPORT TAB */}
        <TabsContent value="export" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Export Cards */}
            {[
              { type: "vehicles", title: "Vehicle Fleet Report", description: "All vehicles with warranty status", icon: Truck },
              { type: "drivers", title: "Driver Roster Report", description: "All drivers with license status", icon: Users },
              { type: "service_records", title: "Service History Report", description: "Complete maintenance records", icon: Wrench },
              { type: "fuel_records", title: "Fuel Consumption Report", description: "All fuel records with costs", icon: Fuel },
              { type: "repair_orders", title: "Repair Orders Report", description: "All repair orders by status", icon: AlertTriangle },
              { type: "cost_summary", title: "Cost Summary Report", description: `Monthly breakdown for ${selectedYear}`, icon: DollarSign },
            ].map((report) => (
              <Card key={report.type} className="bg-card border-border rounded-sm hover:border-primary/50 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <report.icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-heading text-sm text-foreground">{report.title}</h3>
                        <p className="text-xs font-mono text-muted-foreground mt-1">{report.description}</p>
                      </div>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleExport(report.type)}
                    variant="outline"
                    className="w-full mt-4 border-border rounded-sm font-mono text-xs"
                    data-testid={`export-${report.type}-btn`}
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    EXPORT CSV
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Export Info */}
          <Card className="bg-accent/30 border-border rounded-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <FileText className="w-5 h-5 text-primary" />
              <div>
                <p className="text-sm font-medium text-foreground">Export Format Information</p>
                <p className="text-xs font-mono text-muted-foreground">
                  All reports are exported in CSV format, compatible with Excel, Google Sheets, and other spreadsheet applications.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
